// src/portals/fan/pages/HomePage/HomePage.js
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import {
  mockPosts,
  mockAds,
  featuredCreators,
  trendingCreators,
  hotMedia
} from "../../../../data/mockData";
import PostCard from "../../../../components/shared/PostCard";
import CreatorPreviewCard from "./components/CreatorPreviewCard";
import GlobalSearch from "../../../../components/GlobalSearch";
import NotificationCenter from "../../../../components/NotificationCenter";
import { Search, Music, Laugh, Palette, Film, UtensilsCrossed, Smile, Laptop, Gamepad2, ExternalLink, Zap } from "lucide-react";

const contentFilters = [
  { id: "all", label: "All", icon: null },
  { id: "Music", label: "Music", icon: Music },
  { id: "Comedy", label: "Comedy", icon: Laugh },
  { id: "Art & Design", label: "Art", icon: Palette },
  { id: "Film & Video", label: "Film", icon: Film },
  { id: "Food & Drink", label: "Food", icon: UtensilsCrossed },
  { id: "Lifestyle", label: "Lifestyle", icon: Smile },
  { id: "Tech", label: "Tech", icon: Laptop },
  { id: "Gaming", label: "Gaming", icon: Gamepad2 },
];

const AdCard = ({ ad }) => (
  <div className="bg-slate-900 rounded-[2rem] shadow-2xl border border-white/10 overflow-hidden mb-6 group">
    <div className="p-4 flex items-center justify-between bg-white/5 backdrop-blur-sm">
      <div className="flex items-center gap-2">
        <span className="bg-primary/20 text-primary text-[10px] font-black px-2 py-0.5 rounded-full border border-primary/30 uppercase tracking-widest">Sponsored</span>
        <span className="text-xs font-bold text-white/90">{ad.advertiser}</span>
      </div>
      <ExternalLink size={14} className="text-slate-500 group-hover:text-primary transition-colors" />
    </div>
    {ad.mediaUrl && (
      <div className="relative overflow-hidden aspect-[16/9]">
        <img src={ad.mediaUrl} alt={ad.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-slate-900 to-transparent"></div>
      </div>
    )}
    <div className="p-5">
      <h3 className="font-extrabold text-white text-lg mb-1 tracking-tight leading-tight">{ad.title}</h3>
      <p className="text-xs text-slate-400 mb-4 line-clamp-2 leading-relaxed">{ad.content}</p>
      <a
        href={ad.link}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full text-center bg-primary text-white font-black py-3 rounded-2xl transition-all text-[11px] uppercase tracking-widest shadow-lg shadow-primary/20 active:scale-[0.98] hover:neon-glow"
      >
        {ad.cta}
      </a>
    </div>
  </div>
);

const HomePage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFeed, setActiveFeed] = useState("explore");
  const [activeFilter, setActiveFilter] = useState("all");
  const [openPostId, setOpenPostId] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const { currentUser } = useAuth();

  const handleToggleComments = (postId) => {
    setOpenPostId(openPostId === postId ? null : postId);
  };

  const filteredCreators = featuredCreators.filter((creator) =>
    creator.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const followingCreators = useMemo(() => {
    if (!currentUser?.following) return [];
    // In a real app, we'd fetch these creator profiles. 
    // For now, we'll filter featuredCreators + mockPosts creators to find matches
    const allCreators = [...featuredCreators];
    mockPosts.forEach(post => {
      if (!allCreators.find(c => c.username === post.creator.username)) {
        allCreators.push(post.creator);
      }
    });
    return allCreators.filter(c => currentUser.following.includes(c.username));
  }, [currentUser]);

  const followingFeedPosts = mockPosts.filter((post) =>
    currentUser?.following?.includes(post.creator.username)
  );

  const generalFeedPosts = useMemo(() => {
    let posts = activeFilter === "all" 
      ? mockPosts 
      : mockPosts.filter((post) => post.category === activeFilter);
    
    // Inject ads every 5 posts
    const postsWithAds = [];
    posts.forEach((post, index) => {
      postsWithAds.push({ type: 'post', data: post });
      if ((index + 1) % 5 === 0) {
        const adIndex = Math.floor((index + 1) / 5) - 1;
        if (mockAds[adIndex % mockAds.length]) {
          postsWithAds.push({ type: 'ad', data: mockAds[adIndex % mockAds.length] });
        }
      }
    });
    return postsWithAds;
  }, [activeFilter]);

  return (
    <div className="flex flex-col h-full bg-[#fafbff] relative overflow-hidden">
      {/* Decorative Background Glows */}
      <div className="absolute top-[-10%] left-[-10%] w-64 h-64 bg-primary/10 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-[-5%] right-[-5%] w-80 h-80 bg-blue-500/10 rounded-full blur-[120px] pointer-events-none"></div>

      {/* Header with Search and Notifications - GLASSMORPHISM */}
      <div className="sticky top-0 z-30 bg-white/70 backdrop-blur-xl border-b border-white/20">
        <div className="px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-extrabold bg-gradient-to-r from-primary to-indigo-600 bg-clip-text text-transparent tracking-tighter">
            Adnaki
          </h1>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowSearch(true)}
              className="p-2.5 bg-slate-100/50 hover:bg-white text-slate-600 rounded-full transition-all shadow-sm active:scale-95"
            >
              <Search size={20} />
            </button>
            <NotificationCenter />
          </div>
        </div>
      </div>

      {/* Feed Toggle - GLASS STYLE */}
      <div className="sticky top-[73px] z-20 flex px-4 pt-1 pb-3 bg-white/70 backdrop-blur-xl border-b border-white/20">
        <div className="flex-1 flex bg-slate-100 p-1 rounded-2xl">
          <button
            onClick={() => setActiveFeed("explore")}
            className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all ${
              activeFeed === "explore"
                ? "bg-white text-primary shadow-sm scale-[1.02]"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Explore
          </button>
          <button
            onClick={() => setActiveFeed("following")}
            className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all ${
              activeFeed === "following"
                ? "bg-white text-primary shadow-sm scale-[1.02]"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Following
          </button>
        </div>
      </div>

      <div className="flex-grow overflow-y-auto p-4 space-y-8 no-scrollbar">
        {activeFeed === "explore" ? (
          <>
            {/* Featured Creators Horizontal Scroll */}
            <section className="space-y-4">
              <div className="flex justify-between items-end px-1">
                 <h2 className="text-lg font-black text-slate-800 tracking-tight">Rising Stars</h2>
                 <button className="text-[10px] font-black uppercase tracking-widest text-primary">See All</button>
              </div>
              <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
                {filteredCreators.map((creator) => (
                  <CreatorPreviewCard key={creator.id} creator={creator} />
                ))}
              </div>
            </section>

            {/* BIG DISCOVERY CARD */}
            <div className="relative h-64 rounded-[2.5rem] overflow-hidden group shadow-2xl shadow-primary/20">
               <img 
                  src="https://images.unsplash.com/photo-1493225255756-d9584f8606e9?w=800" 
                  alt="Discovery" 
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
               />
               <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
               <div className="absolute bottom-6 left-8 right-8">
                  <div className="flex items-center gap-2 mb-2">
                     <span className="bg-amber-400 text-slate-900 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter">Featured</span>
                     <span className="text-white/60 text-xs font-bold">New Release</span>
                  </div>
                  <h3 className="text-3xl font-black text-white leading-tight tracking-tighter mb-4">Gigi Kiya Live in Addis</h3>
                  <button className="bg-white text-slate-900 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest active:scale-95 transition-all">
                     Get Limited Tickets
                  </button>
               </div>
            </div>

            {/* TRENDING CHARTS SECTION */}
            <section className="space-y-4">
               <div className="flex justify-between items-end px-1">
                  <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase tracking-widest">Trending Charts</h2>
                  <div className="flex gap-2">
                     <div className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400"><Laugh size={14} /></div>
                     <div className="w-8 h-8 rounded-full bg-slate-900 border border-slate-900 flex items-center justify-center text-white shadow-lg"><Music size={14} /></div>
                  </div>
               </div>
               
               <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden divide-y divide-slate-50">
                  {trendingCreators.map((item, index) => (
                    <Link to={`/fan/creator/${item.username}`} key={item.id} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
                       <span className="text-sm font-black text-slate-300 w-4">{index + 1}</span>
                       <img src={item.avatarUrl} alt="" className="w-12 h-12 rounded-full object-cover shadow-sm bg-slate-100" />
                       <div className="flex-1 min-w-0">
                          <p className="text-sm font-black text-slate-900 truncate">{item.name}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{item.category}</p>
                       </div>
                       <div className="text-right">
                          <p className="text-[10px] font-black text-emerald-500">{item.trend}</p>
                          <p className="text-[10px] font-bold text-slate-400">{item.followers}</p>
                       </div>
                    </Link>
                  ))}
               </div>
            </section>

            {/* HOT MEDIA HORIZONTAL */}
            <section className="space-y-4">
               <div className="flex justify-between items-end px-1">
                  <h2 className="text-lg font-black text-slate-800 tracking-tight">Hot on Adnaki</h2>
               </div>
               <div className="flex gap-4 overflow-x-auto no-scrollbar pb-4">
                  {hotMedia.map(media => (
                    <div key={media.id} className="shrink-0 w-48 space-y-3">
                       <div className="relative aspect-square rounded-3xl overflow-hidden shadow-lg group">
                          <img src={media.imageUrl} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors"></div>
                          <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-md text-white text-[9px] font-black px-1.5 py-0.5 rounded-md">
                             {media.duration}
                          </div>
                          {media.type === 'Video' && (
                             <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="w-10 h-10 bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center border border-white/50 text-white">
                                   <Zap size={18} fill="currentColor" />
                                </div>
                             </div>
                          )}
                       </div>
                       <div>
                          <p className="text-[13px] font-black text-slate-900 leading-tight mb-1 truncate">{media.title}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{media.creator}</p>
                       </div>
                    </div>
                  ))}
               </div>
            </section>

            {/* FILTERS - REPOSITIONED */}
            <div className="space-y-4">
                <div className="px-1 font-black text-[10px] uppercase tracking-[0.2em] text-slate-400">Discover by Genre</div>
                <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-3 px-1">
                  {contentFilters.map((filter) => {
                    const IconComponent = filter.icon;
                    const isActive = activeFilter === filter.id;
                    return (
                      <button
                        key={filter.id}
                        onClick={() => setActiveFilter(filter.id)}
                        className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all shrink-0 border-2 ${
                          isActive
                            ? "bg-slate-900 border-slate-900 text-white shadow-xl shadow-slate-200"
                            : "bg-white/80 backdrop-blur-sm border-white text-slate-400 hover:text-slate-900 hover:border-slate-200"
                        }`}
                      >
                        {IconComponent && <IconComponent size={14} strokeWidth={3} />}
                        <span>{filter.label}</span>
                      </button>
                    );
                  })}
                </div>
            </div>

            {/* FEED */}
            <div className="space-y-6">
              <div className="px-1 font-black text-lg text-slate-800 tracking-tight">Main Feed</div>
              {generalFeedPosts.length > 0 ? (
                generalFeedPosts.map((item, index) => {
                  if (item.type === 'ad') {
                    return <AdCard key={`ad-${index}`} ad={item.data} />;
                  }
                  const post = item.data;
                  return (
                    <div key={post.id} className="relative">
                      {post.isBoosted && (
                         <div className="absolute -top-2 -right-2 z-10 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-black px-2.5 py-1 rounded-full shadow-lg flex items-center gap-1 border border-white/20 uppercase tracking-tighter">
                           <Zap size={10} fill="currentColor" />
                           Boosted
                         </div>
                      )}
                      <PostCard
                        post={post}
                        isCommentsVisible={openPostId === post.id}
                        onToggleComments={() => handleToggleComments(post.id)}
                      />
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-slate-500 py-8">
                  No posts found in this category yet.
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="space-y-6">
            {/* Following Avatars - STORY STYLE */}
             <div className="flex gap-4 overflow-x-auto no-scrollbar pb-3 border-b border-slate-100/50">
               {followingCreators.length > 0 ? (
                 followingCreators.map((creator) => (
                   <Link
                     key={creator.username}
                     to={`/fan/creator/${creator.username}`}
                     className="flex-shrink-0 w-16 flex flex-col items-center gap-2 group"
                   >
                     <div className="relative p-0.5 bg-gradient-to-tr from-primary to-blue-500 rounded-full transition-transform active:scale-95 shadow-sm">
                       <img
                         src={creator.profilePicture || creator.profilePictureUrl}
                         alt={creator.name}
                         className="w-12 h-12 rounded-full object-cover border-2 border-white"
                       />
                     </div>
                     <p className="text-[10px] font-bold text-center truncate w-full text-slate-800 tracking-tight">
                       {creator.name}
                     </p>
                   </Link>
                 ))
               ) : (
                 <p className="text-[11px] text-slate-400 font-bold italic px-2">Discover creators in Explore!</p>
               )}
             </div>

            <div className="space-y-4">
              {followingFeedPosts.length > 0 ? (
                followingFeedPosts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    isCommentsVisible={openPostId === post.id}
                    onToggleComments={() => handleToggleComments(post.id)}
                  />
                ))
              ) : (
                <p className="text-center text-slate-500 pt-8">
                  Your following feed is empty. Explore and follow creators to see
                  their posts here!
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Global Search Modal */}
      {showSearch && <GlobalSearch onClose={() => setShowSearch(false)} />}
    </div>
  );
};

export default HomePage;
